import React from 'react';
import { ArrowRight, Home, Trees, Building2, Store, School, MoreHorizontal, Check, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { track, waLink, leadWaMessage, DEFAULT_CONTACT } from '../../lib/site';

export const PROJECT_TYPES = [
  { label: 'House', icon: Home },
  { label: 'Farmhouse', icon: Trees },
  { label: 'Villa', icon: Building2 },
  { label: 'Commercial', icon: Store },
  { label: 'Institutional', icon: School },
  { label: 'Other', icon: MoreHorizontal },
];

export const AREAS = ['Below 1,000 sq.ft', '1,000\u20131,500 sq.ft', '1,500\u20132,500 sq.ft', '2,500\u20135,000 sq.ft', 'Above 5,000 sq.ft', 'Not decided yet'];
export const FLOORS = ['Ground Floor', 'G+1', 'G+2', 'G+3 or above', 'Not decided'];
export const STAGES = ['Just exploring', 'Planning', 'Architectural design ready', 'Foundation completed', 'Construction ready to start', 'Existing project / conversion'];

export const STEP_TITLES = [
  'WHAT ARE YOU PLANNING TO BUILD?',
  'APPROXIMATE BUILT-UP AREA?',
  'HOW MANY FLOORS?',
  'WHERE IS YOUR PROJECT?',
  'WHAT STAGE IS YOUR PROJECT IN?',
  'WHERE SHOULD WE SEND YOUR PROJECT RESPONSE?',
];

export const OptionCard = ({ selected, onClick, children, testId, className = '' }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    aria-pressed={selected}
    className={`select-card text-left p-5 rounded-xl border ${
      selected
        ? 'border-[#155E6D] bg-[#155E6D]/[0.05] shadow-[0_12px_40px_rgba(21,94,109,0.10)]'
        : 'border-border bg-card hover:border-foreground/30'
    } ${className}`}
  >
    {children}
  </button>
);

export const TypeStep = ({ form, onSelect }) => (
  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
    {PROJECT_TYPES.map((p) => {
      const Icon = p.icon;
      const selected = form.project_type === p.label;
      return (
        <OptionCard
          key={p.label}
          selected={selected}
          testId={`estimator-type-${p.label.toLowerCase()}`}
          onClick={() => onSelect(p.label)}
          className="flex flex-col gap-3 items-start min-h-[104px]"
        >
          <Icon className={`w-5 h-5 ${selected ? 'text-[#155E6D]' : 'text-muted-foreground'}`} strokeWidth={1.75} />
          <span className="font-medium text-sm">{p.label}</span>
        </OptionCard>
      );
    })}
  </div>
);

export const AreaStep = ({ form, set, onSelect, onManualContinue }) => (
  <div className="mt-8">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {AREAS.map((a, idx) => (
        <OptionCard
          key={a}
          selected={form.built_up_area === a && !form.custom_area}
          testId={`estimator-area-${idx}`}
          onClick={() => onSelect(a)}
        >
          <span className="font-medium text-sm">{a}</span>
        </OptionCard>
      ))}
    </div>
    <div className="mt-5 flex gap-3 items-center">
      <Input
        type="number"
        inputMode="numeric"
        placeholder="Or enter area manually (sq.ft)"
        value={form.custom_area}
        onChange={(e) => set('custom_area')(e.target.value)}
        data-testid="estimator-area-manual-input"
        className="h-12"
      />
      <Button
        data-testid="estimator-area-manual-continue"
        className="h-12 px-5 shrink-0"
        disabled={!form.custom_area.trim()}
        onClick={onManualContinue}
      >
        Continue
      </Button>
    </div>
  </div>
);

export const FloorsStep = ({ form, onSelect }) => (
  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
    {FLOORS.map((fl, idx) => (
      <OptionCard key={fl} selected={form.floors === fl} testId={`estimator-floors-${idx}`} onClick={() => onSelect(fl)}>
        <span className="font-medium text-sm">{fl}</span>
      </OptionCard>
    ))}
  </div>
);

export const LocationStep = ({ form, set, canContinue, onContinue }) => (
  <div className="mt-8 space-y-4 max-w-md">
    <div>
      <label htmlFor="est-city" className="text-sm font-medium">City / Town</label>
      <Input id="est-city" value={form.city} onChange={(e) => set('city')(e.target.value)} placeholder="e.g. Bengaluru" className="h-12 mt-1.5" data-testid="estimator-city-input" />
    </div>
    <div>
      <label htmlFor="est-district" className="text-sm font-medium">District <span className="text-muted-foreground font-normal">(optional)</span></label>
      <Input id="est-district" value={form.district} onChange={(e) => set('district')(e.target.value)} placeholder="e.g. Bengaluru Rural" className="h-12 mt-1.5" data-testid="estimator-district-input" />
    </div>
    <div>
      <label htmlFor="est-state" className="text-sm font-medium">State</label>
      <Input id="est-state" value={form.state} onChange={(e) => set('state')(e.target.value)} placeholder="e.g. Karnataka" className="h-12 mt-1.5" data-testid="estimator-state-input" />
    </div>
    <Button data-testid="estimator-location-continue" className="h-12 px-6 w-full sm:w-auto" disabled={!canContinue} onClick={onContinue}>
      Continue <ArrowRight className="w-4 h-4 ml-1" />
    </Button>
  </div>
);

export const StageStep = ({ form, onSelect }) => (
  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
    {STAGES.map((s, idx) => (
      <OptionCard key={s} selected={form.project_stage === s} testId={`estimator-stage-${idx}`} onClick={() => onSelect(s)}>
        <span className="font-medium text-sm">{s}</span>
      </OptionCard>
    ))}
  </div>
);

export const ContactStep = ({ form, set, error, submitting, canSubmit, onSubmit }) => (
  <div className="mt-8 space-y-4 max-w-md">
    <div>
      <label htmlFor="est-name" className="text-sm font-medium">Name</label>
      <Input id="est-name" value={form.name} onChange={(e) => set('name')(e.target.value)} placeholder="Your name" className="h-12 mt-1.5" data-testid="estimator-name-input" />
    </div>
    <div>
      <label htmlFor="est-phone" className="text-sm font-medium">Mobile / WhatsApp</label>
      <Input id="est-phone" type="tel" inputMode="tel" value={form.phone} onChange={(e) => set('phone')(e.target.value)} placeholder="+91" className="h-12 mt-1.5" data-testid="estimator-phone-input" />
    </div>
    <div>
      <label htmlFor="est-email" className="text-sm font-medium">Email <span className="text-muted-foreground font-normal">(optional)</span></label>
      <Input id="est-email" type="email" value={form.email} onChange={(e) => set('email')(e.target.value)} placeholder="you@example.com" className="h-12 mt-1.5" data-testid="estimator-email-input" />
    </div>
    <label className="flex items-start gap-3 text-sm cursor-pointer select-none" htmlFor="est-consent">
      <Checkbox
        id="est-consent"
        checked={form.consent}
        onCheckedChange={(v) => set('consent')(!!v)}
        data-testid="estimator-consent-checkbox"
        className="mt-0.5"
      />
      <span className="text-muted-foreground leading-relaxed">
        I agree to be contacted by MELOPA INFRA (Melopa Invents Private Limited) regarding my project enquiry.
      </span>
    </label>
    {error && (
      <p className="text-sm text-destructive" role="alert" data-testid="estimator-error-message">{error}</p>
    )}
    <Button
      data-testid="estimator-submit-button"
      className="w-full h-12 text-sm font-medium shadow-[0_10px_30px_rgba(21,94,109,0.18)]"
      disabled={!canSubmit}
      onClick={onSubmit}
    >
      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
      REQUEST PROJECT ESTIMATE
    </Button>
    <p className="text-xs text-muted-foreground text-center">No spam. Only project-related communication.</p>
  </div>
);

export const SuccessScreen = ({ submitted, onClose }) => (
  <div data-testid="estimator-success-screen">
    <div className="w-14 h-14 rounded-full bg-[#155E6D]/10 border border-[#155E6D]/25 flex items-center justify-center">
      <Check className="w-6 h-6 text-[#155E6D]" />
    </div>
    <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-6">
      PROJECT REQUEST
      <br />
      RECEIVED.
    </h2>
    <p className="mt-4 text-muted-foreground leading-relaxed max-w-md">
      Our team will review your project details and contact you regarding the suitable construction approach.
    </p>

    <div className="mt-8 rounded-xl border border-border bg-card p-6" data-testid="estimator-project-profile">
      <p className="font-mono-tech text-[11px] tracking-[0.24em] uppercase text-[#B87333]">Project Profile</p>
      <dl className="mt-4 space-y-2.5 text-sm">
        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Project type</dt><dd className="font-medium">{submitted.project_type}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Built-up area</dt><dd className="font-medium">{submitted.built_up_area || 'Not decided'}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Floors</dt><dd className="font-medium">{submitted.floors || 'Not decided'}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Location</dt><dd className="font-medium text-right">{[submitted.city, submitted.district, submitted.state].filter(Boolean).join(', ') || '\u2014'}</dd></div>
        <div className="flex justify-between gap-4"><dt className="text-muted-foreground">System</dt><dd className="font-medium">EPS Wall + Roof System</dd></div>
        <div className="flex justify-between gap-4 pt-2 border-t border-border"><dt className="text-muted-foreground">Reference</dt><dd className="font-mono-tech text-xs">{submitted.lead_id}</dd></div>
      </dl>
    </div>

    <a
      href={waLink(DEFAULT_CONTACT.whatsapp, leadWaMessage(submitted))}
      target="_blank"
      rel="noreferrer"
      data-testid="estimator-whatsapp-continue-button"
      onClick={() => track('lead_whatsapp_continued', { lead_id: submitted.lead_id })}
      className="mt-6 w-full h-12 inline-flex items-center justify-center gap-2 rounded-lg bg-[#155E6D] text-white text-sm font-medium hover:bg-[#155E6D]/90 transition-[background-color]"
    >
      <MessageCircle className="w-4 h-4" /> CONTINUE ON WHATSAPP
    </a>
    <button
      type="button"
      data-testid="estimator-done-button"
      onClick={onClose}
      className="mt-3 w-full h-11 rounded-lg border border-border text-sm hover:bg-muted transition-[background-color]"
    >
      Back to website
    </button>
  </div>
);
